import React, { useState, useEffect, useCallback } from 'react';
import {
  IconButton,
  Container,
  Stack,
  Typography,
  Box,
  Backdrop,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { useAuthContext } from 'src/auth/hooks';
import { getOrganizationId } from 'src/services/user.service';
import { useSettingsContext } from 'src/components/settings';
import { useRouter } from 'src/routes/hooks';
import {
  fetchCandidates,
  getCandidatesFilterSummary,
  getFilteredCandidates,
} from 'src/services/candidate.service';
import { useQuery } from 'src/hooks/use-query';
import { useBoolean } from 'src/hooks/use-boolean';
import JobCandidateProfile from 'src/sections/candidate-profile';
import { debounce } from 'lodash';
import { useSnackbar } from 'notistack';
import EmptyState from 'src/sections/empty-state';
import Loader from 'src/components/loader';
import CandidateRow from '../components/candidate-row';
import CandidateToolbar from '../components/candidate-toolbar';
import CandidatePagination from '../components/candidate-pagination';
import CandidateFilters from '../components/candidate-filters';
import FilterChips from '../components/filter-chips';

const SORT_MAP = {
  NONE: {
    label: 'None',
    value: 'AI_INTERVIEW_RANK-ASC',
    arrow: '',
  },
  LATEST: {
    label: 'Latest',
    value: 'CANDIDATE_CREATE_DATE-DESC',
    arrow: 'down',
  },
  OLDEST: {
    label: 'Oldest',
    value: 'CANDIDATE_CREATE_DATE-ASC',
    arrow: 'up',
  },
  A_TO_Z: {
    label: 'A to Z',
    value: 'NAME-ASC',
    arrow: 'down',
  },
  Z_TO_A: {
    label: 'Z to A',
    value: 'NAME-DESC',
    arrow: 'up',
  },
};

const CandidateListView = () => {
  // variables

  // context
  const settings = useSettingsContext();
  const { user } = useAuthContext();
  const organization_id = getOrganizationId(user);
  const rowsPerPage = 10;
  const backendRowsPerPage = 100;

  // states
  const isLoading = useBoolean(true);
  const isEmptyState = useBoolean(false);
  const [filteredCandidateList, setFilteredCandidateList] = useState([]);
  const [backendPage, setBackendPage] = useState(1);
  const [candidates, setCandidates] = useState([]);
  const [filters, setFilters] = useState(null);
  const [totalCandidates, setTotalCandidates] = useState(0);
  const [selectedCandidateIndex, setSelectedCandidateIndex] = useState(0);
  const [previousCandidateId, setPreviousCandidateId] = useState('');
  const [nextCandidateId, setNextCandidateId] = useState('');
  const [showCandidateProfile, setShowCandidateProfile] = useState(false);
  const [candidatesCurrentPage, setCandidatesCurrentPage] = useState(1);
  const [candidateSearchText, setCandidateSearchText] = useState('');
  const [sortBy, setSortBy] = useState('NONE');
  const [showFilterResults, setShowFilterResults] = useState(false);
  const [showFiltersBar, setShowFiltersBar] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [isCandidateProfileLoading, setIsCandidateProfileLoading] =
    useState(false);
  const [showBackdropLoader, setShowBackdropLoader] = useState(false);

  // router
  const router = useRouter();

  // hooks
  const searchQuery = useQuery();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const queryShowCandidateProfile = searchQuery.get('show-candidate-profile');

  // functions
  const getCandidatesList = useCallback(
    async (
      page,
      _rowsPerPage,
      candidateSearch,
      showLoader = true,
      type = 'CANDIDATES',
    ) => {
      try {
        if (showLoader) isLoading.onTrue();

        const payload = {};

        const filter_map = {};

        let data;

        if (type === 'CANDIDATES') {
          if (candidateSearch) {
            filter_map.NAME = candidateSearch;
          }

          payload.filter_map = filter_map;

          data = await fetchCandidates(
            organization_id,
            page,
            _rowsPerPage,
            payload,
          );
        } else if (type === 'FILTER_CANDIDATES') {
          selectedFilters.forEach(code => {
            filter_map[code] = { int_value: 1 };
          });

          payload.filter_map = filter_map;

          data = await getFilteredCandidates(
            organization_id,
            _rowsPerPage,
            page,
            payload,
          );
        }

        if (data !== undefined && data !== null && data.success) {
          const _candidates = data?.body?.candidates?.map((candidate, idx) => {
            const index = page * _rowsPerPage - _rowsPerPage + idx + 1;
            const id = candidate?.user_id;
            const name = candidate?.name;
            const email = candidate?.email;
            const phoneNumber = candidate?.phone_number;
            const source = candidate?.candidate_source;
            const status = candidate?.candidate_status;
            const skills = candidate?.skills;
            const currentOrg = candidate?.current_org;
            const currentRole = candidate?.current_role;
            const lastEducationDegree = candidate?.last_education_degree;
            const lastEducationInstitute = candidate?.last_education_institute;
            const experience = candidate?.total_experience;
            const appliedFor = candidate?.job_openings?.map(job => {
              const jobId = job?.job_opening_id;
              const jobName = job?.name;
              const jobStatus = job?.status;

              return {
                jobId,
                jobName,
                jobStatus,
              };
            });

            const profileBlob = candidate?.profile_blob;
            const profileJson = candidate?.profile_json;
            const resumeUrl = candidate?.resume_url;
            const tags = candidate?.tags;
            const domains = candidate?.domains;
            const countryOfResidence = candidate?.country_of_residence;
            const currentLocationCity = candidate?.current_location_city;

            return {
              index,
              id,
              name,
              email,
              phoneNumber,
              source,
              status,
              skills,
              currentOrg,
              currentRole,
              lastEducationDegree,
              lastEducationInstitute,
              experience,
              appliedFor,
              profileBlob,
              profileJson,
              resumeUrl,
              tags,
              domains,
              countryOfResidence,
              currentLocationCity,
            };
          });

          setTotalCandidates(data.body.total_candidates);
          setCandidates(_candidates);
        } else throw new Error('Something went wrong');
      } catch (err) {
        console.log(err);
      } finally {
        if (type === 'FILTER_CANDIDATES') {
          setShowFilterResults(true);
        }
        isLoading.onFalse();
      }
    },
    [isLoading, organization_id, selectedFilters],
  );

  const handleSearchCandidate = debounce(e => {
    const { value } = e.target;
    setCandidateSearchText(value);

    if (!showFilterResults) {
      setCandidatesCurrentPage(1);
      setCandidates([]);
      getCandidatesList(1, rowsPerPage, value);
    }
  }, 500);

  const getSearchFilteredCandidates = useCallback(
    list => {
      if (!showFilterResults) return list;

      const value = candidateSearchText.toLowerCase().trim();
      const _candidates = list.filter(candidate =>
        candidate.name.toLowerCase().includes(value),
      );

      return _candidates;
    },
    [candidateSearchText, showFilterResults],
  );

  const handlePageChange = useCallback(
    async (event, page) => {
      const _backendPage = Math.ceil((page * rowsPerPage) / backendRowsPerPage);

      if (_backendPage !== backendPage) {
        enqueueSnackbar({
          variant: 'info',
          message: 'Loading more candidates...',
          key: 'loading-more-candidates',
          persist: true,
        });
        await getCandidatesList(_backendPage, backendRowsPerPage, '', false);
        closeSnackbar('loading-more-candidates');
        setBackendPage(_backendPage);
        setCandidatesCurrentPage(page);
      } else {
        setCandidatesCurrentPage(page);
      }
    },
    [backendPage, closeSnackbar, enqueueSnackbar, getCandidatesList],
  );

  const onViewProfile = candidateIndex => {
    setShowCandidateProfile(true);
    setSelectedCandidateIndex(candidateIndex);
    setIsCandidateProfileLoading(true);
  };

  const onPreviousHandler = useCallback(async () => {
    setIsCandidateProfileLoading(true);
    const prevIndex = selectedCandidateIndex - 1;
    const prevPage = Math.floor(prevIndex / rowsPerPage);

    if (candidatesCurrentPage !== prevPage)
      await handlePageChange(null, prevPage);

    setSelectedCandidateIndex(prevIndex);
  }, [candidatesCurrentPage, handlePageChange, selectedCandidateIndex]);

  const onNextHandler = useCallback(async () => {
    setIsCandidateProfileLoading(true);
    const nextIndex = selectedCandidateIndex + 1;
    const nextPage = Math.ceil(nextIndex / rowsPerPage);

    if (candidatesCurrentPage !== nextPage)
      await handlePageChange(null, nextPage);

    setSelectedCandidateIndex(prevIndex => prevIndex + 1);
  }, [candidatesCurrentPage, handlePageChange, selectedCandidateIndex]);

  const getFilters = useCallback(async () => {
    try {
      const response = await getCandidatesFilterSummary(organization_id);
      if (response.success) {
        const result = {};

        response.body.filters.forEach(item => {
          const { filter_group, filter_code, filter_name, candidate_count } =
            item;

          if (!result[filter_group]) {
            result[filter_group] = [];
          }

          const prefix = filter_group;
          const nameWithoutPrefix = filter_name.slice(prefix.length);
          const nameParts = nameWithoutPrefix.split('_');

          const name = nameParts.join(' ');

          result[filter_group].push({
            code: filter_code,
            group: filter_group,
            name,
            count: candidate_count,
          });
        });

        Object.keys(result).forEach(group => {
          result[group].sort((a, b) => a.name.localeCompare(b.name));
        });

        setFilters({
          SKILLS: result?.SKILLS || [],
          DOMAINS: result?.DOMAINS || [],
          TAGS: result?.TAGS || [],
          CANDIDATE_SOURCE: result?.CANDIDATE_SOURCE || [],
        });
      } else throw new Error(response.message);
    } catch (err) {
      console.log(err);
    }
  }, [organization_id]);

  const refetchCandidates = useCallback(
    type => {
      setCandidates([]);
      setBackendPage(1);
      setCandidatesCurrentPage(1);
      getCandidatesList(1, backendRowsPerPage, '', true, type);
    },
    [getCandidatesList],
  );

  // effects
  useEffect(() => {
    getFilters();
  }, [getFilters]);

  useEffect(() => {
    const type = selectedFilters.length ? 'FILTER_CANDIDATES' : 'CANDIDATES';
    refetchCandidates(type);
  }, [selectedFilters.length]);

  useEffect(() => {
    if (isLoading.value) {
      isEmptyState.onFalse();
    } else if (!isLoading.value) {
      if (showFilterResults) {
        const hasFilteredCandidates =
          getSearchFilteredCandidates(candidates).length;

        if (hasFilteredCandidates) {
          isEmptyState.onFalse();
        } else {
          isEmptyState.onTrue();
        }
      } else if (candidates.length) {
        isEmptyState.onFalse();
      } else {
        isEmptyState.onTrue();
      }
    }
  }, [
    candidates.length,
    getSearchFilteredCandidates,
    isLoading.value,
    showFilterResults,
  ]);

  return (
    <Container
      sx={{
        pt: '42.5px',
        pb: '24px',
        px: '40px',
      }}
      maxWidth={settings.themeStretch ? false : 'lg'}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb="28px"
      >
        <Typography variant="h2" sx={{ fontWeight: 600 }}>
          Talent pool
        </Typography>
        {/* <Button
          variant="contained"
          startIcon={<Iconify icon="tabler:plus" />}
          onClick={() => router.push(paths.dashboard.candidates.new)}
        >
          Add candidate
        </Button> */}
      </Stack>
      <CandidateToolbar
        filters={filters}
        handleSearchCandidate={handleSearchCandidate}
        showFiltersBar={showFiltersBar}
        setShowFiltersBar={setShowFiltersBar}
        totalCandidates={totalCandidates}
        SORT_MAP={SORT_MAP}
        sortBy={sortBy}
        setSortBy={setSortBy}
        isShowingFilterResult={!!filteredCandidateList.length}
      />

      {filters && showFiltersBar && (
        <CandidateFilters
          filters={filters}
          setFilters={setFilters}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />
      )}

      {selectedFilters.length > 0 ? (
        <FilterChips
          filters={filters}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
        />
      ) : null}

      {totalCandidates > 0 && showFilterResults ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            width: 'min-content',
            alignItems: 'center',
            mb: '20px',
            p: '7.5px 10px 7.5px 16px',
            borderRadius: '6px',
            gap: '8px',
            backgroundColor: theme => theme.palette.background.primaryAccent2,
          }}
        >
          <Typography
            variant="h4"
            fontWeight={600}
            borderRadius="6px"
            noWrap
            color="content.accent1"
          >
            Filter Results {`${totalCandidates}`}
          </Typography>
          <IconButton
            onClick={() => {
              setShowFilterResults(false);
              setSelectedFilters([]);
            }}
          >
            <Iconify
              sx={{
                color: theme => theme.palette.content.accent1,
              }}
              icon="tabler:x"
            />
          </IconButton>
        </Box>
      ) : null}

      <Stack spacing="16px" minHeight="calc(100vh - 250px)">
        {isLoading.value && (
          <Stack width="100%" alignItems="center" justifyContent="center">
            <Typography variant="input">Loading...</Typography>
          </Stack>
        )}

        {!isLoading.value && isEmptyState.value && (
          <Stack m="auto">
            <EmptyState />
          </Stack>
        )}

        {!isEmptyState.value &&
          !isLoading.value &&
          getSearchFilteredCandidates(candidates)
            .slice(
              ((candidatesCurrentPage - 1) % rowsPerPage) * rowsPerPage,
              ((candidatesCurrentPage - 1) % rowsPerPage) * rowsPerPage +
                rowsPerPage,
            )
            .map(candidate => (
              <CandidateRow
                key={candidate.id}
                candidate={candidate}
                filters={filters}
                selectedFilters={selectedFilters}
                onViewRow={() => {
                  onViewProfile(candidate.index);
                }}
              />
            ))}
      </Stack>

      {!isLoading.value ? (
        <Stack
          width="100%"
          pb="17px"
          pt="30px"
          alignItems="center"
          justifyContent="center"
        >
          <CandidatePagination
            currentPage={candidatesCurrentPage}
            handlePageChange={handlePageChange}
            totalPages={Math.ceil(totalCandidates / rowsPerPage)}
          />
        </Stack>
      ) : null}

      {candidates.length ? (
        <JobCandidateProfile
          candidateType="CANDIDATE"
          open={queryShowCandidateProfile || showCandidateProfile}
          setOpen={setShowCandidateProfile}
          candidateId={
            queryShowCandidateProfile || selectedCandidateIndex !== 0
              ? candidates.find(
                  candidate => candidate.index === selectedCandidateIndex,
                ).id
              : null
          }
          onClose={() => {
            setPreviousCandidateId(0);
            setNextCandidateId(0);
            setSelectedCandidateIndex(0);
            setShowCandidateProfile(false);
          }}
          hasPrevious={
            queryShowCandidateProfile ? false : selectedCandidateIndex > 1
          }
          hasNext={
            queryShowCandidateProfile
              ? false
              : selectedCandidateIndex < totalCandidates
          }
          onPrevious={onPreviousHandler}
          onNext={onNextHandler}
          isLoading={isCandidateProfileLoading}
          setIsLoading={setIsCandidateProfileLoading}
          setShowCandidateProfile={setShowCandidateProfile}
        />
      ) : null}
    </Container>
  );
};

export default CandidateListView;
